import { createClaimsTable } from '../utils/claimUtils';
import styled, { createGlobalStyle } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { useMsal } from '@azure/msal-react';
import { protectedResources } from '../authConfig';
import useFetchWithMsal from '../hooks/useFetchWithMsal';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import cssConfig from '../cssConfig';
const GlobalStyles = createGlobalStyle`
    html, body {
        margin: 0;
        padding: 0;
        height: 100%;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
        background-color: #F9FAFB;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }

    body {
        min-height: 100vh;
    }

    #root {
        min-height: 100vh;
        height: 100%;
        margin: 0;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
        background-color: #F9FAFB;
        display: flex;
        flex-direction: column;
    }

    * {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
        box-sizing: border-box;
    }
`;

const PageContainer = styled.div`
    flex: 1;
    background: #F9FAFB;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-x: hidden;
    padding-top: 40px;

    @media (max-width: 768px) {
        padding-top: 0px;
        background: #F6F6F6;
    }
`;

const LogoContainer = styled.div`
    position: absolute;
    z-index: 2;
    padding: 0 32px;
    top: 6%;
    left: 4%;
    width: 100%;

    @media (max-width: 768px) {
        padding: 0 16px;
        top: 5%;
    }
`;

const Logo = styled.img`
    height: 32px;
    width: 172px;
    object-fit: contain;
    filter: brightness(0) invert(1);
    content: url(${cssConfig.appsLogoUrl});

    @media (max-width: 768px) {
        height: 24px;
        width: 140px;
        content: url(${cssConfig.appsLogoMobileUrl});
    }
`;

const MainContentWrapper = styled.div`
    max-width: 1000px;
    margin: 0 auto;
    width: 100%;
    padding: 0 32px;
    position: relative;
    flex: 1;

    @media (max-width: 768px) {
        padding: 0;
    }
`;

const TopImageBanner = styled.div`
    height: 15%;
    border-top-left-radius: 24px;
    border-top-right-radius: 24px;
    overflow: hidden;
    position: relative;
    margin-bottom: 0;

    @media (max-width: 768px) {
        border-radius: 0;
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-image: url(${cssConfig.mobileCoverDesktopUrl});
        background-size: cover;
        background-position: center;
        filter: brightness(0.9);

        @media (max-width: 768px) {
            background-image: url(${cssConfig.mobileCoverUrl});
        }
    }
`;

const MainContent = styled.div`
    position: relative;
    z-index: 1;
    margin-top: -40px;
    height: 80%;
`;

const ContentCard = styled.div`
    background: white;
    border-radius: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 40px;
    margin-bottom: 0px;
    margin-top: -24px;
    height: 95%;

    @media (max-width: 768px) {
        padding: 24px;
        border-radius: 16px;
        margin-top: -16px;
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
        height: 90%;
        box-shadow: none;
    }
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 0px;
`;

const TopHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 32px;

    @media (max-width: 768px) {
        margin-bottom: 24px;
    }
`;

const HeaderTitle = styled.h1`
    font-size: 22px;
    font-weight: 600;
    color: #111827;
    margin: 0;
    line-height: 27px;
    letter-spacing: -0.5px;

    @media (max-width: 768px) {
        font-size: 20px;
        line-height: 24px;
    }
`;

const ConnectIdText = styled.div`
    color: ${cssConfig.headerTextColor};
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0px;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 4px 0;

    &::before {
        content: '';
        display: ${props => props.showIcon ? 'block' : 'none'};
        width: 18px;
        height: 18px;
        background-image: url(${props => props.iconUrl});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
    }

    @media (max-width: 768px) {
        display: none;
    }
`;

const UserSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 40px;
    padding-bottom: 40px;
    border-bottom: 1px solid #E5E7EB;

    @media (max-width: 768px) {
        margin-bottom: 24px;
        padding-bottom: 24px;
    }
`;

const UserInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;

    @media (max-width: 768px) {
        gap: 8px;
    }
`;

const UserTextInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const UserAvatar = styled.img`
    width: 42px;
    height: 42px;
    border-radius: 50%;
    border: 2px solid white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

    @media (max-width: 768px) {
        width: 36px;
        height: 36px;
    }
`;

const UserName = styled.h2`
    font-size: 14px;
    font-weight: 600;
    color: #151718;
    margin: 0;
    line-height: 20px;
    letter-spacing: 0px;
`;

const UserDescription = styled.p`
    font-size: 14px;
    font-weight: 400;
    color: #151718;
    margin: 0;
    line-height: 20px;
    letter-spacing: 0px;
`;

const SubHeader = styled.p`
    font-size: 12px;
    color: #8B8B8B;
    font-weight: 400;
    margin: 0 0 20px 0;
    line-height: 18px;
    letter-spacing: 0px;
`;

const AppGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;

    @media (min-width: 1024px) {
        grid-template-columns: repeat(4, 1fr);
    }

    @media (max-width: 768px) {
        gap: 16px;
    }
`;

const AppCard = styled.div`
    background: white;
    border: 1px solid #E5E7EB;
    border-radius: 12px;
    padding: 20px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;
    gap: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    &:hover {
        border-color: #D1D5DB;
    }

    @media (max-width: 768px) {
        padding: 16px;
        gap: 12px;
    }
`;

const AppCardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;

const AppIcon = styled.img`
    width: 40px;
    height: 40px;
    border-radius: 8px;

    @media (max-width: 768px) {
        width: 32px;
        height: 32px;
        border-radius: 6px;
    }
`;

const ExternalLinkIcon = styled(FontAwesomeIcon)`
    color: #9CA3AF;
    font-size: 14px;
`;

const AppName = styled.h3`
    font-size: 15px;
    font-weight: 500;
    color: #111827;
    margin: 0;
`;

const AppDescription = styled.p`
    color: #6B7280;
    font-size: 14px;
    line-height: 1.5;
    margin: 0;
`;

const SettingsIcon = styled.img`
    cursor: pointer;
    padding: 8px;
    border-radius: 6px;
    transition: all 0.2s ease;
    opacity: 0.7;

    &:hover {
        background-color: #F3F4F6;
    }

    @media (max-width: 768px) {
        position: absolute;
        right: 24px;
        top: 18px;
    }
`;

const Footer = styled.div`
    color: #C6C6C6;
    font-size: 11px;
    padding: 24px 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;

    @media (max-width: 768px) {
        display: none;
    }
`;

const MobileSaveSection = styled.div`
    display: none;
    
    @media (max-width: 768px) {
        display: flex;
        align-items: center;
        gap: 16px;
        background: #F6F6F6;
        border: 1px solid #EAEAEA;
        border-radius: 8px;
        padding: 24px;
        margin: 24px;
    }
`;

const SaveIcon = styled.img`
    width: 24px;
    height: 24px;
    object-fit: contain;
`;

const SaveText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const SaveTitle = styled.div`
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: ${cssConfig.textColor};
`;

const SaveDescription = styled.div`
    font-size: 12px;
    line-height: 20px;
    font-weight: 400;
    color: #8B8B8B;
`;

const PoweredBy = styled.div`
    display: flex;
    gap: 2px;
    color: #C6C6C6;

    a {
        color: #C6C6C6;

        &:hover {
            text-decoration: none;
        }
    }
`;

const ConnectIdTextFooter = styled.div`
    font-weight: 600;
`;

const LoaderContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    width: 100%;
`;

const Loader = styled.div`
    width: 24px;
    height: 24px;
    border: 2px solid #E5E7EB;
    border-top: 2px solid ${cssConfig.buttonColor};
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

const SaveDialog = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 83vh;
    background: white;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    padding: 24px;
    padding-top: 40px;
    transform: translateY(${props => props.isOpen ? '0' : '100%'});
    transition: transform 300ms ease-in-out;
    z-index: 1000;
    overflow-y: none;
`;

const DialogPin = styled.img`
    width: 32px;
    height: 32px;
    margin-bottom: 24px;
`;

const DialogOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    opacity: ${props => props.isOpen ? 1 : 0};
    visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
    transition: opacity 300ms ease-in-out;
    z-index: 999;
`;

const DialogHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    position: relative;
`;

const DialogTitle = styled.h2`
    font-size: 22px;
    font-weight: 600;
    color: ${props => props.cssConfig.textColor};
    margin: 0;
    line-height: 29px;
`;

const SaveCloseButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    position: absolute;
    right: 5%;
    top: 2%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        width: 24px;
        height: 24px;
        object-fit: contain;
    }
`;

const SettingsCloseButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    position: absolute;
    right: 5%;
    top: 2%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        width: 24px;
        height: 24px;
        object-fit: contain;
    }

    @media (min-width: 769px) {
        right: 3%;
        top: 3%;
    }
`;

const DialogContent = styled.div`
    color: ${props => props.cssConfig.textColor};
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    margin-bottom: 40px;
    padding-bottom: 40px;
    border-bottom: 1px solid #EAEAEA;
`;

const PlatformSection = styled.div`
    margin-top: 0;
    padding-bottom: 40px;
    border-bottom: 1px solid #EAEAEA;
    margin-bottom: 40px;
`;

const PlatformTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    line-height: 15px;
    color: ${props => props.cssConfig.textColor};
    margin: 0 0 24px 0;
`;

const StepList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Step = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
`;

const StepText = styled.div`
    font-size: 14px;
    line-height: 24px;
    font-weight: 400;
    color: ${props => props.cssConfig.textColor};

    strong {
        font-weight: 600;
    }
`;

const StepIcon = styled.img`
    width: 24px;
    height: 24px;
    object-fit: contain;
`;

const SettingsDialog = styled.div`
    background: white;
    padding: 24px;
    z-index: 1000;
    overflow-y: auto;

    @media (max-width: 768px) {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        height: 83vh;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        transform: translateY(${props => props.isOpen ? '0' : '100%'});
        transition: transform 300ms ease-in-out;
    }

    @media (min-width: 769px) {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(${props => props.isOpen ? '1' : '0'});
        opacity: ${props => props.isOpen ? '1' : '0'};
        visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
        transition: all 200ms ease-in-out;
        width: 800px;
        border-radius: 16px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }
`;

const SettingsHeader = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 32px;
    padding-top: 8px;

    @media (min-width: 769px) {
        padding-top: 0;
    }
`;

const SettingsLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const SettingsHeaderIcon = styled.img`
    width: 32px;
    height: 32px;
`;

const SettingsTitle = styled.h2`
    font-size: 22px;
    font-weight: 600;
    line-height: 29px;
    color: ${props => props.cssConfig.textColor};
    margin: 0;
`;

const UsernameSection = styled.div`
    text-align: right;
    display: flex;
    flex-direction: column;
    justify-content: end;
    margin-bottom: 2px;
`;

const UsernameLabel = styled.div`
    color: #8B8B8B;
    font-size: 12px;
    line-height: 18px;
    font-weight: 400;
    margin-bottom: 4px;
`;

const Username = styled.div`
    font-size: 16px;
    font-weight: 600;
    line-height: 15px;
    color: ${props => props.cssConfig.textColor};
`;

const SettingsOption = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: #F6F6F6;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s ease;
    width: 100%;

    @media (min-width: 769px) {
        width: 240px;
        justify-content: flex-start;
        padding: 16px;
    }

    &:hover {
        background: #F3F4F6;
    }
`;

const SettingsOptionIcon = styled.img`
    width: 18px;
    height: 18px;
`;

const SettingsOptionText = styled.div`
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: ${props => props.cssConfig.textColor};
`;

const SettingsOptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;

    @media (min-width: 769px) {
        flex-direction: row;
        gap: 16px;
        justify-content: center;
    }
`;

const ConnectIDFooter = styled.div`
    margin-top: 40px;

    @media (min-width: 769px) {
        margin-top: 32px;
    }
`;

const ConnectIDLogo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    font-weight: 500;
    color: ${props => props.cssConfig.headerTextColor};
    font-size: 14px;
    line-height: 20px;
    img {
        width: 18px;
        height: 18px;
    }
`;

const PoweredByText = styled.div`
    color: #8B8B8B;
    font-size: 11px;
    font-weight: 400;

    a {
        color: #8B8B8B;
        text-decoration: underline;

        &:hover {
            text-decoration: none;
        }
    }
`;

export const HomePageDisplay = (props) => {
    const navigate = useNavigate();
    const { instance, inProgress } = useMsal();
    const [ssoData, setSsoData] = useState(null);
    const tokenClaims = createClaimsTable(props.idTokenClaims);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    const getFullName = () => {
        for (const [_, value] of Object.entries(tokenClaims)) {
            if (value[0] === "name") {
                return value[1];
            }
        }
        return "Sarah Thompson";
    }

    const fullName = getFullName();

    const handleLogoutRedirect = () => {
        instance.logoutRedirect();
    };

    const { error, execute, isLoading } = useFetchWithMsal({
        scopes: [
            ...protectedResources.ssoApi.scopes.read,
            ...protectedResources.ssoApi.scopes.admin,
        ]
    });

    // const handleProfileEdit = () => {
    //     if(inProgress === InteractionStatus.None){
    //        instance.acquireTokenRedirect(b2cPolicies.authorities.editProfile);
    //     }
    // };  

    const handleSupport = () => {
        window.open('mailto:support@cocentric.com', '_blank');
    };

    useEffect(() => {
        if (!ssoData) {
            execute("GET", protectedResources.ssoApi.endpoint).then((response) => {
                setSsoData(response);
            });
        }
    }, [execute, ssoData]);

    const openAppUrl = (url) => {
        window.open(url, '_blank').focus();
    }

    return (
        <>
            <Helmet>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link href="https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
            </Helmet>
            <GlobalStyles />
            <PageContainer>
                <MainContentWrapper>
                    <LogoContainer>
                        <Logo alt="Gleneagles Logo" />
                    </LogoContainer>
                    <TopImageBanner />
                    <MainContent>
                        <ContentCard>
                            <Header>
                                <TopHeader>
                                    <HeaderTitle>Your Access Hub</HeaderTitle>
                                    <ConnectIdText 
                                        showIcon={!!cssConfig.userCircleUrl}
                                        iconUrl={cssConfig.userCircleUrl}
                                    >
                                        {cssConfig.headerText}
                                    </ConnectIdText>
                                </TopHeader>
                                <UserSection>
                                    <UserInfo>
                                        <UserAvatar 
                                            src="https://www.w3schools.com/howto/img_avatar.png" 
                                            alt={fullName}
                                        />
                                        <UserTextInfo>
                                            <UserName>{fullName}</UserName>
                                            <UserDescription>Everything you need for work in one place.</UserDescription>
                                        </UserTextInfo>
                                    </UserInfo>
                                    <SettingsIcon 
                                        src="https://eussocontent.blob.core.windows.net/sso-custom-ui/settings-02.png"
                                        alt="Settings"
                                        onClick={() => setIsSettingsOpen(true)}
                                    />
                                </UserSection>
                            </Header>

                            <SubHeader>Tap an app below to get started.</SubHeader>

                            {!isLoading && !error ? (
                                <AppGrid>
                                    {ssoData && ssoData.map((data, i) => (
                                        <AppCard key={i} onClick={() => openAppUrl(data.signInUrl)}>
                                            <AppCardHeader>
                                                <AppIcon src={data.icon} alt={data.name} />
                                                <ExternalLinkIcon icon={faArrowUpRightFromSquare} />
                                            </AppCardHeader>
                                            <div>
                                                <AppName>{data.name}</AppName>
                                                <AppDescription>
                                                    {data.description || 'Access your application'}
                                                </AppDescription>
                                            </div>
                                        </AppCard>
                                    ))}
                                </AppGrid>
                            ) : (
                                <LoaderContainer>
                                    <Loader />
                                </LoaderContainer>
                            )}
                        </ContentCard>
                        <Footer>
                            <ConnectIdTextFooter>ConnectID © 2025 Cocentric Group Limited</ConnectIdTextFooter>
                            <PoweredBy>
                                Powered by <a href="https://cocentric.com/connect-frontline" target="_blank" rel="noopener noreferrer">Connect by Cocentric</a>
                            </PoweredBy>
                        </Footer>
                        <MobileSaveSection onClick={() => setIsDialogOpen(true)}>
                            <SaveIcon 
                                src={cssConfig.pinUrl} 
                                alt="Save icon"
                            />
                            <SaveText>
                                <SaveTitle>Save this page for easy access!</SaveTitle>
                                <SaveDescription>Tap here to see how to bookmark this page or add it to your home screen.</SaveDescription>
                            </SaveText>
                        </MobileSaveSection>
                    </MainContent>
                </MainContentWrapper>

                <DialogOverlay isOpen={isDialogOpen} onClick={() => setIsDialogOpen(false)} />
                <SaveDialog isOpen={isDialogOpen}>
                    <SaveCloseButton onClick={() => setIsDialogOpen(false)}>
                        <img src="https://eussocontent.blob.core.windows.net/sso-custom-ui/x-close.png" alt="Close" />
                    </SaveCloseButton>
                    <DialogPin src={cssConfig.pinMobileUrl} alt="Pin icon" />
                    <DialogHeader>
                        <DialogTitle cssConfig={cssConfig}>Save page for quick access</DialogTitle>
                    </DialogHeader>
                    <DialogContent cssConfig={cssConfig}>
                        Follow the steps below to bookmark this page or add it to your home screen.
                    </DialogContent>
                    
                    <PlatformSection>
                        <PlatformTitle cssConfig={cssConfig}>On iPhone (Safari)</PlatformTitle>
                        <StepList>
                            <Step>
                                <StepIcon 
                                    src="https://eussocontent.blob.core.windows.net/sso-custom-ui/quickaccess1.png" 
                                    alt="Step 1"
                                />
                                <StepText cssConfig={cssConfig}>Tap the <strong>Share</strong> icon.</StepText>
                            </Step>
                            <Step>
                                <StepIcon 
                                    src="https://eussocontent.blob.core.windows.net/sso-custom-ui/quickaccess2.png" 
                                    alt="Step 2"
                                />
                                <StepText cssConfig={cssConfig}>Scroll down and tap <strong>Add to Home Screen</strong></StepText>
                            </Step>
                            <Step>
                                <StepIcon 
                                    src="https://eussocontent.blob.core.windows.net/sso-custom-ui/quickaccess3.png" 
                                    alt="Step 3"
                                />
                                <StepText cssConfig={cssConfig}>Tap <strong>Add</strong> in the top-right corner.</StepText>
                            </Step>
                        </StepList>
                    </PlatformSection>

                    <PlatformSection>
                        <PlatformTitle cssConfig={cssConfig}>On Android (Chrome)</PlatformTitle>
                        <StepList>
                            <Step>
                                <StepIcon 
                                    src="https://eussocontent.blob.core.windows.net/sso-custom-ui/quickaccess4.png" 
                                    alt="Step 1"
                                />
                                <StepText cssConfig={cssConfig}>Tap the <strong>three dots</strong> in the top-right corner.</StepText>
                            </Step>
                            <Step>
                                <StepIcon 
                                    src="https://eussocontent.blob.core.windows.net/sso-custom-ui/quickaccess5.png" 
                                    alt="Step 2"
                                />
                                <StepText cssConfig={cssConfig}>Tap <strong>Add to Home Screen</strong>.</StepText>
                            </Step>
                            <Step>
                                <StepIcon 
                                    src="https://eussocontent.blob.core.windows.net/sso-custom-ui/quickaccess6.png" 
                                    alt="Step 3"
                                />
                                <StepText cssConfig={cssConfig}>Tap <strong>Add</strong>.</StepText>
                            </Step>
                        </StepList>
                    </PlatformSection>
                </SaveDialog>

                <DialogOverlay isOpen={isSettingsOpen} onClick={() => setIsSettingsOpen(false)} />
                <SettingsDialog isOpen={isSettingsOpen}>
                    <SettingsCloseButton onClick={() => setIsSettingsOpen(false)}>
                        <img src="https://eussocontent.blob.core.windows.net/sso-custom-ui/x-close.png" alt="Close" />
                    </SettingsCloseButton>
                    
                    <SettingsHeader>
                        <SettingsLeft>
                            <SettingsHeaderIcon src={cssConfig.settingsIconUrl} alt="Settings" />
                            <SettingsTitle cssConfig={cssConfig}>Settings</SettingsTitle>
                        </SettingsLeft>
                        <UsernameSection>
                            <UsernameLabel>Username</UsernameLabel>
                            <Username cssConfig={cssConfig}>{fullName}</Username>
                        </UsernameSection>
                    </SettingsHeader>

                    <SettingsOptionsContainer>
                        {/* ADO #4078: this option had no handler at all - a dead button on every tenant's
                            Access Hub. It now goes to the same reset page the sign-in screen links to. */}
                        <SettingsOption
                            role="button"
                            tabIndex={0}
                            onClick={() => navigate('/forgotPassword')}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/forgotPassword'); } }}>
                            <SettingsOptionIcon
                                src="https://eussocontent.blob.core.windows.net/sso-custom-ui/lock-01.png"
                                alt="Password Reset"
                            />
                            <SettingsOptionText cssConfig={cssConfig}>Password Reset</SettingsOptionText>
                        </SettingsOption>

                        <SettingsOption onClick={handleSupport}>
                            <SettingsOptionIcon 
                                src="https://eussocontent.blob.core.windows.net/sso-custom-ui/help-circle.png"
                                alt="Contact Support"
                            />
                            <SettingsOptionText cssConfig={cssConfig}>Contact Support</SettingsOptionText>
                        </SettingsOption>

                        <SettingsOption onClick={handleLogoutRedirect}>
                            <SettingsOptionIcon 
                                src="https://eussocontent.blob.core.windows.net/sso-custom-ui/log-out-04.png"
                                alt="Log Out"
                            />
                            <SettingsOptionText cssConfig={cssConfig}>Log Out</SettingsOptionText>
                        </SettingsOption>
                    </SettingsOptionsContainer>

                    <ConnectIDFooter>
                        <ConnectIDLogo cssConfig={cssConfig}>
                            <img src={cssConfig.userCircleUrl} alt="ConnectID" />
                            ConnectID
                        </ConnectIDLogo>
                        <PoweredByText>
                            Powered by <a href="https://cocentric.com/connect-frontline" target="_blank" rel="noopener noreferrer">Connect by Cocentric</a>
                        </PoweredByText>
                    </ConnectIDFooter>
                </SettingsDialog>
            </PageContainer>
        </>
    );
};
 